import { getUrlField } from '../helper';

/**
 * Most basic content fields, needed for most use cases.
 *
 * Current use cases regarding contents:
 *  - Library
 *     -> only use case without signed covers, to speed up the initial load
 *  - Content creation (and new version)
 *  - Content details
 *  - Content details editor
 *  - Content preview:
 *      - Library
 *      - Room creation (library, reorder step)
 *      - Room overview (contact overview)
 *      - Insights
 *  - Create room
 *  - Provide reference (view contact's provided references)
 *  - Public content (public links)
 *  - Rooms (contacts view)
 */
const contentBaseFields = (includeSignedUrls: boolean) => `
  ${getUrlField(includeSignedUrls, 'cover', 'signedCover')}
  ${getUrlField(includeSignedUrls, 'defaultCover', 'signedDefaultCover')}
  id
  archived
  coverType
  createdAt
  createdAtEpoch
  deletedAt
  description
  downloadsCount
  expired
  isArchived
  isInternal
  libraryVisible
  mtData {
    provider
  }
  name
  personal
  questionId
  sectionHeaderId
  shareable
  shared
  status
  type
  updatedAt
  updatedAtEpoch
  userDownloadsCount
  userViewsCount
  version
  views
  parentId
  contact {
    contactable
    email
    firstName
    id
    lastName
    linkedin
    company {
      name
    }
  }
  embeddable {
    url
  }
  storageSync {
    preference
    status
  }
  text {
    text
    html
  }
  url {
    url
  }
  user {
    id
    fullName
    username
  }
`;

/**
 * Fields containing the actual content's media (audio, document, text or video).
 *
 * Current use cases:
 *  - Content details
 *  - Content details editor
 *  - Content preview
 *  - Create rooms (review step, preview room)
 *  - Provide reference (view contact's provided references)
 *  - Public content (public links)
 *  - Rooms (contacts view)
 *  - Room overview (for the content overview: charts and time spent in media)
 *
 * @note For performance purposes: avoid fetching these fields if the
 * media won't be available right away. For example: the library listing
 * doesn't require fetching the media as part of the initial load, since
 * users can only play it if they open the preview or go to the content's details
 */
const contentMediaFields = `
  audio {
    downloadable
    totalSeconds
    url
    version
  }
  document {
    downloadable
    filesize
    height
    mime
    pages
    pdfUrl
    url
    width
  }
  embeddable {
    embeddedContent
    url
  }
  image {
    downloadable
    height
    mime
    predominantColor
    signedUrl
    width
  }
  url {
    iframe
    openGraph
    url
  }
  video {
    captions {
      url
      language
      languageCode
    }
    downloadable
    downloadUrl
    totalSeconds
    uri
    url
    version
  }
`;

const contentSearchFields = `
  attributes
  fuzzyMatch
  highlight
  searchScore
  tags
`;

/**
 * These fields are required wherever `contentMediaFields` are used, as they
 * contain the processing log for the content's media.
 * @note currently only used for document contents
 * @todo DSR-2485: Add back this when the feature is ready to release,
 *       also remove the eslint-disable comment
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const contentProcessingFields = `
  processingLog {
    processor
    status
  }
`;

/**
 * Minimum required fields for listing the contents. These include
 * the content attributes and tags fields, that allow to build filters.
 *
 * Current use cases:
 *  - Library (with lazy signed covers)
 */
export const contentListingFields = `
  ${contentBaseFields(false)}
  ${contentSearchFields}
`;

/**
 * Minimum required fields for listing the contents. These include
 * the content attributes and tags fields, that allow to build filters.
 *
 * Current use cases:
 *  - Create rooms (select content step)
 */
export const contentListingSignedFields = `
  ${contentBaseFields(true)}
  ${contentSearchFields}
`;

/**
 * Minimum required fields for viewing a piece of content. These correspond
 * to the combined base and media fields, to allow users to play/view the
 * media right away.
 *
 * Current use cases:
 *  - Content details
 *  - Content details editor
 *  - Content preview
 *  - Create room (select content step)
 *  - Provide reference (view contact's provided references)
 */
export const contentViewFields = `
  ${contentBaseFields(true)}
  ${contentMediaFields}
  coverType
  link
  sharedRoomIds
  mtData {
    provider
    contentId
    isSnippet
    snippetInfo {
      id
      startTime
      endTime
    }
  }
  storageProviderData {
    copyLink
    provider
    viewLink
  }
  storageSync {
    preference
  }
`;

/**
 * Fields that contain an array of the content's media versions. For example:
 * if a user uploaded 3 different video versions for a single content, the
 * "videos" fields will have the urls for each of those versions.
 *
 * Current use cases:
 *  - Room overview (for the content overview: charts and time spent in media)
 *
 * @note For performance purposes: avoid loading these fields if the
 * information from the different versions is not required.
 */
export const contentMediaVersionFields = `
  ${contentViewFields}
  audios {
    downloadable
    totalSeconds
    url
    version
  }
  documents {
    downloadable
    filesize
    height
    mime
    pages
    pdfUrl
    url
    version
    width
  }
  embeddable {
    embeddedContent
    url
    version
  }
  images {
    downloadable
    height
    mime
    predominantColor
    signedUrl
    version
    width
  }
  url {
    iframe
    openGraph
    url
  }
  videos {
    downloadable
    totalSeconds
    url
    version
  }
`;

/**
 * Fields required for viewing and editing the contents.
 *
 * Current use cases:
 *  - Content creation (and new version)
 *  - Content details
 *  - Content details editor
 */
export const contentDetailsFields = `
  ${contentViewFields}
  attributes
  requestedReference {
    id
  }
  storageSync {
    newVersion {
      id
      name
      link
      version
    }
    preference
    provider
    status
  }
  tags
  updateable
  uuid
`;

/**
 * Minimum required fields for bulk editing contents. These doesn't
 * include most field included at the base fields (not required).
 *
 * Current use cases:
 *  - Bulk updates
 */
export const contentBulkUpdateFields = `
  ${getUrlField(true, 'cover', 'signedCover')}
  ${getUrlField(true, 'defaultCover', 'signedDefaultCover')}
  id
  attributes
  audio {
    downloadable
  }
  coverType
  description
  document {
    downloadable
  }
  image {
    downloadable
  }
  name
  personal
  sectionHeaderId
  shareable
  status
  storageSync {
    preference
  }
  tags
  type
  user {
    id
    fullName
  }
  url {
    iframe
    openGraph
  }
  video {
    downloadable
  }
`;

/**
 * Minimum required fields for listing the contents for insights. These
 * don't include some fields from the content base fields, as they are
 * not used.
 *
 * Current use cases:
 *  - Insights (displaying the info of each piece of content)
 */
export const contentInsightsFields = `
  id
  archived
  attributes
  coverType
  createdAtEpoch
  name
  shareable
  shared
  status
  type
  userId
  views
  contact {
    id
    email
    firstName
    lastName
    company {
      name
    }
  }
  embeddable {
    url
  }
  url {
    iframe
  }
  user {
    id
    fullName
  }
  text {
    text
  }
`;

export const contentCoversInsightsFields = `
  ${getUrlField(true, 'cover', 'signedCover')}
  ${getUrlField(true, 'defaultCover', 'signedDefaultCover')}
  id
`;

/**
 * Minimum required fields for listing the contents for insights in vendor mode. These
 * don't include most of the fields from the content base fields, as they are
 * not used.
 *
 * Current use cases:
 *  - Insights
 *    - Rooms
 *      - Content Used in Won Deals
 */
export const contentInsightsFieldsVendorMode = `
  id
  name
  type
`;

/**
 * Fields required for viewing contents created and updated by.
 *
 * Current use cases:
 *  - Content library
 *  - Content details
 */
export const contentCreatedByFields = `
  createdBy {
    firstName
    lastName
  }
  updatedBy {
    firstName
    lastName
  }
`;

/**
 * Minimum required fields for viewing a piece of content with the base
 * media fields. For public links, like rooms, the content's cover and media
 * images are fetched pre-signed at once, instead of being lazy-loaded as in
 * the library or other internal (private) views.
 *
 * Current use cases:
 *  - Public content (public links)
 *  - Rooms (contacts view)
 */
export const contentRoomViewFields = `
  ${contentBaseFields(true)}
  ${contentMediaFields}
  coverType
  link
  sharedRoomIds
  storageProviderData {
    copyLink
    provider
    viewLink
  }
  storageSync {
    preference
  }
`;

export const contentDocumentProcessingFields = `
  ${contentProcessingFields}
  ${contentMediaFields}
`;
